import { useState, useEffect } from 'react';
import { Plus, Edit, Eye, X, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AgencyModal from './AgencyModal';

interface Agency {
  id: string;
  name: string;
  company: string;
  has_custom_objectives: boolean;
  monthly_objectives: Record<string, Record<string, number>> | null;
  annual_objectives?: {
    fiscal_year: number;
    annual_objective: number;
    accounting_objective: number;
  }[];
  negotiators_objective_sum?: number;
  current_annual_objective?: number;
}

interface ObjectivesModalProps {
  isOpen: boolean;
  onClose: () => void;
  agency: Agency | null;
  negotiators: Array<{
    firstname: string;
    lastname: string;
    annual_objective: number;
    full_annual_objective?: number;
    objective_percentage?: number;
    has_custom_objectives: boolean;
    status: string;
  }>;
}

function ObjectivesModal({ isOpen, onClose, agency, negotiators }: ObjectivesModalProps) {
  if (!isOpen || !agency) return null;

  // Calculate current fiscal year
  const today = new Date();
  const currentFiscalYear = today.getMonth() + 1 < 4 ? today.getFullYear() - 1 : today.getFullYear();

  // Use the pre-calculated current_annual_objective from the agency object
  const currentAnnualObjective = agency.current_annual_objective || 0;

  // Get accounting objective for current fiscal year
  const currentAccountingObjective = agency.annual_objectives?.find(obj => obj.fiscal_year === currentFiscalYear)?.accounting_objective || 0;

  // Calculate total negotiator objectives (already weighted by objective_percentage)
  const totalNegotiatorObjectives = negotiators
    .filter(n => n.firstname !== 'DIRECTION')
    .reduce((sum, n) => sum + n.annual_objective, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'expert':
        return 'bg-red-100 text-red-800';
      case 'senior':
        return 'bg-orange-100 text-orange-800';
      case 'confirmé':
        return 'bg-yellow-100 text-yellow-800';
      case 'animateur':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const objectiveDifference = totalNegotiatorObjectives - currentAnnualObjective;
  const isDifferenceNegative = objectiveDifference < 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl relative max-h-[92vh] flex flex-col shadow-2xl">
        {/* Dark gradient header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Eye size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">Objectifs — {agency.name}</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>
        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Objectif agence */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-xs font-medium text-slate-500 mb-1">Objectif Agence</h3>
              <p className="text-2xl font-bold text-slate-800">
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0
                }).format(currentAnnualObjective)}
              </p>
            </div>

            {/* Objectifs négociateurs */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-xs font-medium text-slate-500 mb-1">Total Objectifs Négociateurs</h3>
              <p className="text-2xl font-bold text-slate-800">
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0
                }).format(totalNegotiatorObjectives)}
              </p>
              <p className={`text-sm font-semibold ${isDifferenceNegative ? 'text-red-600' : 'text-green-600'}`}>
                {isDifferenceNegative ? 'Manque' : 'Excédent'} : {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                  maximumFractionDigits: 0
                }).format(Math.abs(objectiveDifference))}
              </p>
            </div>
          </div>

          {/* Liste des négociateurs */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Négociateur</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">% Objectif</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objectif Agence</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objectif Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {negotiators
                    .filter(n => n.firstname !== 'DIRECTION')
                    .map((negotiator, index) => (
                      <tr key={index} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-800">
                          {negotiator.firstname} {negotiator.lastname}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(negotiator.status)}`}>
                            {negotiator.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                          {negotiator.objective_percentage}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 0
                          }).format(negotiator.annual_objective)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 0
                          }).format(negotiator.full_annual_objective || negotiator.annual_objective)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AgencySettings() {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isObjectivesModalOpen, setIsObjectivesModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [selectedAgencyNegotiators, setSelectedAgencyNegotiators] = useState<Array<{
    firstname: string;
    lastname: string;
    annual_objective: number;
    full_annual_objective?: number;
    objective_percentage?: number;
    has_custom_objectives: boolean;
    status: string;
    monthly_objectives?: Record<string, Record<string, number>>;
  }>>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Calculate current fiscal year
      const today = new Date();
      const currentFiscalYear = today.getMonth() + 1 < 4 ? today.getFullYear() - 1 : today.getFullYear();

      // Load agencies with their objectives
      const { data: agenciesData, error: agenciesError } = await supabase
        .from('agencies')
        .select(`
          *,
          agency_annual_objectives!left (
            fiscal_year,
            annual_objective,
            accounting_objective
          )
        `)
        .order('company')
        .order('name');

      if (agenciesError) throw agenciesError;

      // Load negotiator objectives for each agency
      const agenciesWithObjectives = await Promise.all((agenciesData || []).map(async (agency) => {
        const { data: negotiatorsData, error: negotiatorsError } = await supabase
          .from('negotiators')
          .select(`
            id,
            firstname,
            lastname,
            annual_objective,
            has_custom_objectives,
            monthly_objectives,
            negotiator_annual_objectives!left (
              fiscal_year,
              annual_objective
            ),
            negotiator_agencies!inner (
              agency,
              objective_percentage
            )
          `)
          .eq('negotiator_agencies.agency', agency.name)
          .eq('archived', false)
          .neq('firstname', 'DIRECTION');

        if (negotiatorsError) throw negotiatorsError;

        console.log(`[loadData] Agency: ${agency.name}, Negotiators:`, negotiatorsData?.length);

        // Calculate total negotiator objectives WITH objective_percentage applied
        const totalObjectives = (negotiatorsData || []).reduce((sum, negotiator) => {
          let fullObjective = 0;

          if (negotiator.has_custom_objectives && negotiator.monthly_objectives?.[currentFiscalYear]) {
            // Sum monthly objectives for current fiscal year
            fullObjective = Object.values(negotiator.monthly_objectives[currentFiscalYear])
              .reduce((monthSum, value) => monthSum + (value || 0), 0);
          } else {
            // Use annual objective from negotiator_annual_objectives
            const annualObjective = negotiator.negotiator_annual_objectives?.find(
              obj => obj.fiscal_year === currentFiscalYear
            );
            fullObjective = annualObjective?.annual_objective || 0;
          }

          // IMPORTANT: Filter to get ONLY the association for THIS agency
          const agencyAssociation = negotiator.negotiator_agencies.find(na => na.agency === agency.name);
          const objectivePercentage = agencyAssociation?.objective_percentage || 0;

          // Apply the percentage to get the agency-specific objective
          const agencySpecificObjective = (fullObjective * objectivePercentage) / 100;

          console.log(`  - ${negotiator.firstname} ${negotiator.lastname}:`, {
            fullObjective,
            objectivePercentage,
            agencySpecificObjective,
            allAssociations: negotiator.negotiator_agencies.map(na => ({ agency: na.agency, pct: na.objective_percentage }))
          });

          return sum + agencySpecificObjective;
        }, 0);

        console.log(`  => Total objectives for ${agency.name}: ${totalObjectives}`);

        // Calculate agency's annual objective
        let annualObjective = 0;
        if (agency.has_custom_objectives && agency.monthly_objectives?.[currentFiscalYear]) {
          annualObjective = Object.values(agency.monthly_objectives[currentFiscalYear])
            .reduce((sum, value) => sum + (value || 0), 0);
        } else {
          const objective = agency.agency_annual_objectives?.find(
            obj => obj.fiscal_year === currentFiscalYear
          );
          annualObjective = objective?.annual_objective || 0;
        }

        return {
          ...agency,
          negotiators_objective_sum: totalObjectives,
          current_annual_objective: annualObjective
        };
      }));

      setAgencies(agenciesWithObjectives);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditClick = (agency: Agency) => {
    setSelectedAgency(agency);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedAgency(null);
    setIsModalOpen(true);
  };

  const handleObjectivesClick = async (agency: Agency) => {
    try {
      // Calculate current fiscal year
      const today = new Date();
      const currentFiscalYear = today.getMonth() + 1 < 4 ? today.getFullYear() - 1 : today.getFullYear();

      console.log('[handleObjectivesClick] Agency data:', {
        name: agency.name,
        has_custom_objectives: agency.has_custom_objectives,
        monthly_objectives: agency.monthly_objectives,
        annual_objectives: agency.annual_objectives,
        current_annual_objective: agency.current_annual_objective
      });

      // Load negotiators with their objectives
      const { data: negotiatorsData, error: negotiatorsError } = await supabase
        .from('negotiators')
        .select(`
          firstname,
          lastname,
          status,
          has_custom_objectives,
          monthly_objectives,
          negotiator_annual_objectives!left (
            fiscal_year,
            annual_objective
          ),
          negotiator_agencies!inner (
            agency,
            objective_percentage,
            start_date,
            end_date
          )
        `)
        .eq('negotiator_agencies.agency', agency.name)
        .eq('archived', false)
        .neq('firstname', 'DIRECTION')
        .order('lastname')
        .order('firstname');

      if (negotiatorsError) throw negotiatorsError;

      console.log(`[Modal] Raw data for ${agency.name}:`, negotiatorsData);

      const formattedNegotiators = (negotiatorsData || [])
        .filter(negotiator => {
          // Filter by date: only show negotiators currently associated with this agency
          const agencyAssociation = negotiator.negotiator_agencies.find(na => na.agency === agency.name);
          if (!agencyAssociation) return false;

          const today = new Date();
          const startDate = agencyAssociation.start_date ? new Date(agencyAssociation.start_date) : null;
          const endDate = agencyAssociation.end_date ? new Date(agencyAssociation.end_date) : null;

          // Check if the association is currently active
          const isActive = (!startDate || startDate <= today) && (!endDate || endDate >= today);
          
          return isActive;
        })
        .map(negotiator => {
          let annualObjective = 0;

          if (negotiator.has_custom_objectives && negotiator.monthly_objectives?.[currentFiscalYear]) {
            // Sum monthly objectives for current fiscal year
            annualObjective = Object.values(negotiator.monthly_objectives[currentFiscalYear])
              .reduce((sum, value) => sum + (value || 0), 0);
          } else {
            // Use annual objective from negotiator_annual_objectives
            const objective = negotiator.negotiator_annual_objectives?.find(
              obj => obj.fiscal_year === currentFiscalYear
            );
            annualObjective = objective?.annual_objective || 0;
          }

          console.log(`[Modal] ${negotiator.firstname} ${negotiator.lastname}:`, {
            negotiator_agencies: negotiator.negotiator_agencies,
            looking_for_agency: agency.name
          });

          // IMPORTANT: Filter to get ONLY the association for THIS agency
          const agencyAssociation = negotiator.negotiator_agencies.find(na => na.agency === agency.name);
          
          console.log(`[Modal] Found association:`, agencyAssociation);
          
          const objectivePercentage = agencyAssociation?.objective_percentage || 0;

          // Apply the percentage to get the agency-specific objective
          const agencySpecificObjective = (annualObjective * objectivePercentage) / 100;

          console.log(`[Modal] Final calculation:`, {
            annualObjective,
            objectivePercentage,
            agencySpecificObjective
          });

          return {
            firstname: negotiator.firstname,
            lastname: negotiator.lastname,
            annual_objective: agencySpecificObjective,
            full_annual_objective: annualObjective,
            objective_percentage: objectivePercentage,
            has_custom_objectives: negotiator.has_custom_objectives,
            status: negotiator.status,
            monthly_objectives: negotiator.monthly_objectives
          };
        });

      console.log(`[Modal] Formatted negotiators:`, formattedNegotiators);

      setSelectedAgency(agency);
      setSelectedAgencyNegotiators(formattedNegotiators);
      setIsObjectivesModalOpen(true);
    } catch (err: any) {
      console.error('Error loading negotiators:', err);
      setError(err.message);
    }
  };

  const handleDeleteClick = async (agency: Agency) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer l'agence ${agency.name} ? Cette action est irréversible.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('agencies')
        .delete()
        .eq('name', agency.name);

      if (error) throw error;

      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getCompanyBadgeColor = (company: string) => {
    switch (company) {
      case 'KI':
        return 'bg-blue-100 text-blue-800';
      case 'KA':
        return 'bg-green-100 text-green-800';
      case 'DRYADE':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getObjectiveDifferenceColor = (agency: Agency) => {
    const negoSum = agency.negotiators_objective_sum || 0;
    if (negoSum === 0) return 'text-gray-500';
    return agency.current_annual_objective! > negoSum ? 'text-red-600' : 'text-green-600';
  };

  const calculateObjectiveDifference = (agency: Agency) => {
    const negoSum = agency.negotiators_objective_sum || 0;
    return negoSum - agency.current_annual_objective!;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-700">{agencies.length}</span> agence{agencies.length !== 1 ? 's' : ''}
        </p>
        <button
          onClick={handleAddClick}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 text-sm font-semibold transition-colors shadow-sm"
        >
          <Plus size={16} />
          <span>Nouvelle agence</span>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Société</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nom de l'agence</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objectif annuel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Obj. Compta</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Objectifs négociateurs</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Rue</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">CP</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ville</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tél.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {agencies.map((agency) => (
                <tr key={agency.id} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${getCompanyBadgeColor(agency.company)}`}>
                      {agency.company}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-800">
                    {agency.name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                    {formatCurrency(agency.current_annual_objective || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                    {formatCurrency(agency.agency_annual_objectives?.[0]?.accounting_objective || 0)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-700">{formatCurrency(agency.negotiators_objective_sum || 0)}</span>
                      <span className={`text-xs font-semibold ${getObjectiveDifferenceColor(agency)}`}>
                        ({formatCurrency(calculateObjectiveDifference(agency))})
                      </span>
                      <button
                        onClick={() => handleObjectivesClick(agency)}
                        className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-500 transition-colors"
                        title="Voir le détail des objectifs"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{agency.street || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{agency.zip_code || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{agency.city || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">{agency.phone || '—'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleEditClick(agency)}
                        className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors"
                        title="Modifier l'agence"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(agency)}
                        className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        title="Supprimer l'agence"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AgencyModal
        agency={selectedAgency}
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedAgency(null); }}
        onSuccess={loadData}
      />

      <ObjectivesModal
        isOpen={isObjectivesModalOpen}
        onClose={() => { setIsObjectivesModalOpen(false); setSelectedAgency(null); setSelectedAgencyNegotiators([]); }}
        agency={selectedAgency}
        negotiators={selectedAgencyNegotiators}
      />
    </div>
  );
}